import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Alert,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Autocomplete,
} from '@mui/material';
import { Edit, Save, Cancel, Add, Delete, ExpandMore, ExpandLess, EditNote, AutoAwesome } from '@mui/icons-material';
import { useStores } from '../../hooks/use-stores';
import LocaleService from '../../services/LocaleService';

const PartnerTranslationsEditor = ({ partnerId, partnerSettings = [] }) => {
    const { superStore } = useStores();
    const [language, setLanguage] = useState('en');
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [changes, setChanges] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [addingNew, setAddingNew] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');
    const [existingExpanded, setExistingExpanded] = useState(true);
    const [commitDialogOpen, setCommitDialogOpen] = useState(false);
    const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
    const [bulkEditKey, setBulkEditKey] = useState('');
    const [bulkEditValues, setBulkEditValues] = useState({});
    const [loadingBulkData, setLoadingBulkData] = useState(false);
    const [languageCache, setLanguageCache] = useState({});
    const [loadingBulkKey, setLoadingBulkKey] = useState(null);
    const [allEnglishKeys, setAllEnglishKeys] = useState([]);
    const [loadingKeys, setLoadingKeys] = useState(false);
    const [translatingAI, setTranslatingAI] = useState(false);

    const languages = superStore.languages || [];

    // Extract translation overrides from partner settings
    const translationOverrides = useMemo(() => {
        const overrides = {};
        partnerSettings.forEach(setting => {
            if (setting.name && setting.name.startsWith('ui.translation.')) {
                const parts = setting.name.split('.');
                if (parts.length >= 4) {
                    const lang = parts[2];
                    const key = parts.slice(3).join('.');
                    
                    if (!overrides[lang]) {
                        overrides[lang] = {};
                    }
                    overrides[lang][key] = setting.value;
                }
            }
        });
        return overrides;
    }, [partnerSettings]);

    // Get current language overrides
    const currentLanguageOverrides = translationOverrides[language] || {};
    
    // Combine existing overrides with pending changes for display
    const displayOverrides = { ...currentLanguageOverrides };
    const changeKey = `common:${language}`;
    if (changes[changeKey]) {
        Object.keys(changes[changeKey]).forEach(key => {
            displayOverrides[key] = changes[changeKey][key];
        });
    }
    
    const existingKeys = Object.keys(displayOverrides).sort();
    const hasChanges = Object.keys(changes).length > 0;

    useEffect(() => {
        // Load languages from superStore if not already loaded
        const loadLanguages = async () => {
            if (!superStore.languages || superStore.languages.length === 0) {
                await superStore.loadLanguages();
            }
        };
        loadLanguages();
    }, []);

    // Update language selector when languages are loaded
    useEffect(() => {
        if (languages.length > 0 && !languages.find(l => l.code === language)) {
            // If current language is not in the list, set to first available or 'en'
            const enLang = languages.find(l => l.code === 'en');
            setLanguage(enLang ? 'en' : languages[0]?.code || 'en');
        }
    }, [languages, language]);

    useEffect(() => {
        // Load all English keys from both namespaces for autocomplete
        const loadEnglishKeys = async () => {
            if (allEnglishKeys.length > 0) return; // Already loaded
            
            setLoadingKeys(true);
            try {
                const [commonKeys, assessmentKeys] = await Promise.all([
                    LocaleService.fetchLocale('en', 'common').catch(() => ({})),
                    LocaleService.fetchLocale('en', 'assessment').catch(() => ({}))
                ]);
                
                const combinedKeys = [
                    ...Object.keys(commonKeys),
                    ...Object.keys(assessmentKeys)
                ].sort();
                
                setAllEnglishKeys(combinedKeys);
            } catch (e) {
                console.error('Error loading English keys:', e);
            } finally {
                setLoadingKeys(false);
            }
        };

        loadEnglishKeys();
    }, [allEnglishKeys.length]);

    const handleEdit = (key, value) => {
        setEditingKey(key);
        setEditValue(value || '');
    };

    const handleSave = (key) => {
        const changeKey = `common:${language}`; // Fixed namespace for partner mode
        const updatedChanges = { ...changes };
        
        if (!updatedChanges[changeKey]) {
            updatedChanges[changeKey] = {};
        }
        
        updatedChanges[changeKey][key] = editValue;
        setChanges(updatedChanges);

        setEditingKey(null);
        setEditValue('');
    };

    const handleCancel = () => {
        setEditingKey(null);
        setEditValue('');
    };

    const handleAddNew = () => {
        if (!newKey.trim()) {
            alert('Please enter a key');
            return;
        }

        const changeKey = `common:${language}`; // Fixed namespace for partner mode
        const updatedChanges = { ...changes };
        
        if (!updatedChanges[changeKey]) {
            updatedChanges[changeKey] = {};
        }
        
        updatedChanges[changeKey][newKey] = newValue;
        setChanges(updatedChanges);

        setAddingNew(false);
        setNewKey('');
        setNewValue('');
    };

    const handleDeleteChange = (changeKey, key) => {
        const updatedChanges = { ...changes };
        delete updatedChanges[changeKey][key];
        
        if (Object.keys(updatedChanges[changeKey]).length === 0) {
            delete updatedChanges[changeKey];
        }
        
        setChanges(updatedChanges);
    };

    const handleSaveAll = () => {
        setCommitDialogOpen(true);
    };

    const handleCommitSave = async () => {
        setCommitDialogOpen(false);
        setLoading(true);
        setError(null);
        setSuccess(false);
        
        try {
            // Transform changes to partner settings format
            const settingsToUpdate = [];
            
            Object.keys(changes).forEach(changeKey => {
                const [ns, lang] = changeKey.split(':');
                Object.keys(changes[changeKey]).forEach(key => {
                    const settingName = `ui.translation.${lang}.${key}`;
                    const settingValue = changes[changeKey][key];
                    
                    settingsToUpdate.push({
                        name: settingName,
                        value: settingValue,
                        partnerId: partnerId
                    });
                });
            });

            // Save settings via superStore
            for (const setting of settingsToUpdate) {
                await superStore.addPartnerSetting(partnerId, setting);
            }
            
            setSuccess(true);
            
            // Reload partner data to refresh settings
            await superStore.loadPartner(partnerId);
            
            // Clear the changes state after successful save
            setChanges({});
        } catch (e) {
            setError(`Failed to save changes: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCommitCancel = () => {
        setCommitDialogOpen(false);
    };

    const handleBulkEdit = async (key) => {
        setBulkEditKey(key);
        setLoadingBulkKey(key);
        
        try {
            // Don't use cache if there are unsaved changes
            const hasUnsavedChanges = Object.keys(changes).some(
                changeKey => changes[changeKey][key] !== undefined
            );
            
            // Check cache first (only if no unsaved changes)
            const cacheKey = `common:${key}`;
            if (!hasUnsavedChanges && languageCache[cacheKey]) {
                setBulkEditValues(languageCache[cacheKey]);
                setBulkEditDialogOpen(true);
                setLoadingBulkKey(null);
                return;
            }
            
            setLoadingBulkData(true);
            
            // Load all language data for this key from overrides
            const allData = {};
            languages.forEach(lang => {
                // First get the saved value from overrides
                let value = translationOverrides[lang.code]?.[key] || '';
                
                // Then check if there's an unsaved change for this language and key
                const changeKey = `common:${lang.code}`;
                if (changes[changeKey] && changes[changeKey][key] !== undefined) {
                    value = changes[changeKey][key];
                }
                
                allData[lang.code] = value;
            });
            
            // Only cache if there are no unsaved changes
            if (!hasUnsavedChanges) {
                setLanguageCache(prev => ({
                    ...prev,
                    [cacheKey]: allData
                }));
            }
            
            setBulkEditValues(allData);
        } catch (e) {
            console.error('Error loading bulk edit data:', e);
            setError('Failed to load language data for bulk edit');
        } finally {
            setLoadingBulkData(false);
            setLoadingBulkKey(null);
            setBulkEditDialogOpen(true);
        }
    };

    const handleBulkEditApply = () => {
        const updatedChanges = { ...changes };
        
        // Add all changed values to pending changes, grouped by language
        Object.keys(bulkEditValues).forEach(langCode => {
            const newValue = bulkEditValues[langCode];
            const originalValue = translationOverrides[langCode]?.[bulkEditKey] || '';
            
            // Only add if value has changed
            if (newValue !== originalValue) {
                const changeKey = `common:${langCode}`;
                
                if (!updatedChanges[changeKey]) {
                    updatedChanges[changeKey] = {};
                }
                
                updatedChanges[changeKey][bulkEditKey] = newValue;
            }
        });
        
        setChanges(updatedChanges);
        
        setBulkEditDialogOpen(false);
        setBulkEditKey('');
        setBulkEditValues({});
    };

    const handleBulkEditCancel = () => {
        setBulkEditDialogOpen(false);
        setBulkEditKey('');
        setBulkEditValues({});
    };

    const handleFillWithAI = async () => {
        setTranslatingAI(true);
        setError(null);

        try {
            // Find empty languages
            const emptyLanguages = languages
                .map(lang => lang.code)
                .filter(code => !bulkEditValues[code] || bulkEditValues[code].trim() === '');

            if (emptyLanguages.length === 0) {
                setError('No empty fields to translate');
                setTranslatingAI(false);
                return;
            }

            // Find first non-empty value (prefer English, otherwise first non-empty)
            let currentValue = null;
            if (bulkEditValues['en'] && bulkEditValues['en'].trim()) {
                currentValue = { lang: 'en', value: bulkEditValues['en'] };
            } else {
                for (const lang of languages) {
                    if (bulkEditValues[lang.code] && bulkEditValues[lang.code].trim()) {
                        currentValue = { lang: lang.code, value: bulkEditValues[lang.code] };
                        break;
                    }
                }
            }

            if (!currentValue) {
                setError('No source text found for translation');
                setTranslatingAI(false);
                return;
            }

            // Call translation API
            const translations = await LocaleService.translateText(emptyLanguages, currentValue);

            // Update bulk edit values with translations
            setBulkEditValues(prev => ({
                ...prev,
                ...translations
            }));

        } catch (e) {
            setError(`Failed to translate: ${e.message || 'Unknown error'}`);
        } finally {
            setTranslatingAI(false);
        }
    };

    const areAllFieldsEmpty = () => {
        return languages.every(lang => 
            !bulkEditValues[lang.code] || bulkEditValues[lang.code].trim() === ''
        );
    };

    const showBottomButton = existingExpanded;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Top Bar - Full Width */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                        Partner Translation Overrides
                    </Typography>

                    {/* Language Selector Only (Namespace hidden in partner mode) */}
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Language</InputLabel>
                                <Select
                                    value={language}
                                    label="Language"
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    {languages.map(lang => (
                                        <MenuItem key={lang.code} value={lang.code}>
                                            {lang.name} ({lang.code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Paper>
                </Box>
            </Box>

            {/* Content Area with Sidebar */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Main Content */}
                <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
                    {/* Alerts */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                            Changes saved successfully!
                        </Alert>
                    )}

                    {/* Add New Button - Top */}
                    {addingNew ? (
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Add New Translation Override
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Autocomplete
                                    freeSolo
                                    options={allEnglishKeys}
                                    value={newKey}
                                    onChange={(event, newValue) => {
                                        setNewKey(newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        setNewKey(newInputValue);
                                    }}
                                    loading={loadingKeys}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Key"
                                            fullWidth
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingKeys ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <TextField
                                    label="Value"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={newValue}
                                    onChange={(e) => setNewValue(e.target.value)}
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<Save />}
                                        onClick={handleAddNew}
                                    >
                                        Add
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Cancel />}
                                        onClick={() => {
                                            setAddingNew(false);
                                            setNewKey('');
                                            setNewValue('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    ) : (
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => setAddingNew(true)}
                            sx={{ mb: 3 }}
                        >
                            Add New Translation Override
                        </Button>
                    )}

                    {/* Existing Overrides Section - Collapsible */}
                    <Paper sx={{ mb: 3 }}>
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                            }}
                            onClick={() => setExistingExpanded(!existingExpanded)}
                        >
                            <Typography variant="h6">
                                Translation Overrides ({existingKeys.length})
                            </Typography>
                            <IconButton size="small">
                                {existingExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Box>
                        <Collapse in={existingExpanded}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '30%', fontWeight: 'bold' }}>Key</TableCell>
                                            <TableCell sx={{ width: '60%', fontWeight: 'bold' }}>Value</TableCell>
                                            <TableCell sx={{ width: '10%', fontWeight: 'bold' }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {existingKeys.map(key => (
                                            <TableRow key={key}>
                                                <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                                    {key}
                                                </TableCell>
                                                <TableCell>
                                                    {editingKey === key ? (
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            rows={3}
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {displayOverrides[key]}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {editingKey === key ? (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleSave(key)}
                                                            >
                                                                <Save />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="secondary"
                                                                onClick={handleCancel}
                                                            >
                                                                <Cancel />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEdit(key, displayOverrides[key])}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleBulkEdit(key)}
                                                                title="Bulk edit across languages"
                                                                disabled={loadingBulkKey === key}
                                                            >
                                                                {loadingBulkKey === key ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <EditNote />
                                                                )}
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>
                    </Paper>

                </Box>

                {/* Right Sidebar - Changes Preview */}
                <Box
                    sx={{
                        width: 400,
                        flexShrink: 0,
                        borderLeft: 1,
                        borderColor: 'divider',
                        p: 2,
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Pending Changes
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {!hasChanges ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                            No pending changes
                        </Typography>
                    ) : (
                        <>
                            <List sx={{ overflow: 'auto', flex: 1 }}>
                                {Object.keys(changes).map(changeKey => (
                                    <Box key={changeKey} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {changeKey}
                                        </Typography>
                                        {Object.keys(changes[changeKey]).map(key => (
                                            <ListItem
                                                key={key}
                                                sx={{
                                                    display: 'block',
                                                    bgcolor: 'grey.50',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                                secondaryAction={
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        size="small"
                                                        onClick={() => handleDeleteChange(changeKey, key)}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 'bold', wordBreak: 'break-word', pr: 4 }}
                                                        >
                                                            {key}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ 
                                                                whiteSpace: 'pre-wrap', 
                                                                wordBreak: 'break-word',
                                                                fontSize: changes[changeKey][key] === '' ? '1.1em' : 'inherit',
                                                                fontStyle: changes[changeKey][key] === '' ? 'italic' : 'normal',
                                                                color: changes[changeKey][key] === '' ? 'text.secondary' : 'inherit'
                                                            }}
                                                        >
                                                            {changes[changeKey][key] === '' ? '<EMPTY>' : changes[changeKey][key]}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </Box>
                                ))}
                            </List>

                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    startIcon={<Save />}
                                    onClick={handleSaveAll}
                                    disabled={loading}
                                >
                                    Save All Changes
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {Object.keys(changes).reduce((acc, key) => acc + Object.keys(changes[key]).length, 0)} change(s)
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {/* Commit Confirmation Dialog */}
            <Dialog open={commitDialogOpen} onClose={handleCommitCancel} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Save</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to save these translation overrides?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCommitCancel}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCommitSave}
                        variant="contained"
                        color="primary"
                        startIcon={<Save />}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Edit Dialog */}
            <Dialog open={bulkEditDialogOpen} onClose={handleBulkEditCancel} maxWidth="md" fullWidth>
                <DialogTitle>Bulk Edit: {bulkEditKey}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Edit this translation key across all languages:
                    </Typography>
                    {loadingBulkData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {languages.map(lang => (
                                <Grid item xs={12} sm={6} key={lang.code}>
                                    <TextField
                                        fullWidth
                                        label={`${lang.name} (${lang.code})`}
                                        multiline
                                        rows={2}
                                        value={bulkEditValues[lang.code] || ''}
                                        onChange={(e) => setBulkEditValues(prev => ({
                                            ...prev,
                                            [lang.code]: e.target.value
                                        }))}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBulkEditCancel} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFillWithAI}
                        variant="outlined"
                        color="primary"
                        startIcon={translatingAI ? <CircularProgress size={16} /> : <AutoAwesome />}
                        disabled={loadingBulkData || translatingAI || areAllFieldsEmpty()}
                        sx={{ mr: 'auto' }}
                    >
                        Fill with AI
                    </Button>
                    <Button
                        onClick={handleBulkEditApply}
                        variant="contained"
                        color="primary"
                        startIcon={<Save />}
                        disabled={loadingBulkData || translatingAI}
                    >
                        Apply Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PartnerTranslationsEditor;
